/**
 * Resolução estrita de destinos ao nível de CIDADE (módulo puro — sem rede,
 * sem IA, sem créditos).
 *
 * Problema real: "Vou a londres 2 dias" resolvia para "Inglaterra, Reino
 * Unido" (região/país) e a rota urbana falhava. Regra: quando o utilizador
 * nomeia uma cidade, só aceitamos resultados de nível localidade
 * (city/town/village/municipality). Estado, região, condado, país ou
 * continente NUNCA são destino válido para uma rota urbana.
 */

import { canonicalCityName, comparableCityName } from "./city-endonym";

const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

/** Chave comparável entre exónimos ("Moscovo"/"Moscou"/"Moscow"). */
const aliasKey = (value: string) => fold(comparableCityName(value ?? ""));

/** Níveis administrativos que nunca podem ser destino de uma rota urbana. */
const NON_CITY_KINDS = new Set([
  "country",
  "state",
  "region",
  "province",
  "county",
  "state_district",
  "continent",
  "territory",
  "administrative",
  "administrative_area_level_1",
  "administrative_area_level_2",
]);

export type NominatimCityItem = {
  place_id?: number | string | null;
  osm_type?: string | null;
  osm_id?: number | string | null;
  lat?: string | number | null;
  lon?: string | number | null;
  addresstype?: string | null;
  type?: string | null;
  class?: string | null;
  namedetails?: Record<string, string> | null;
  address?: Record<string, string | undefined> | null;
};

export const localityFromAddress = (
  address: Record<string, string | undefined> | null | undefined,
): string =>
  address?.city || address?.town || address?.village || address?.municipality || "";

const hasFiniteCoords = (item: NominatimCityItem): boolean =>
  Number.isFinite(Number(item.lat)) && Number.isFinite(Number(item.lon));

/** Nomes conhecidos de um resultado (localidade + endónimos do OSM). */
const namesOf = (item: NominatimCityItem): string[] =>
  [
    localityFromAddress(item.address),
    item.namedetails?.["name:en"] || "",
    item.namedetails?.name || "",
  ].filter(Boolean);

const matchesRequested = (item: NominatimCityItem, requestedName: string): boolean => {
  const target = aliasKey(requestedName);
  if (!target) return false;
  return namesOf(item).some((name) => {
    const candidate = aliasKey(name);
    if (!candidate) return false;
    return (
      candidate === target ||
      candidate.split(" ").includes(target) ||
      target.split(" ").includes(candidate)
    );
  });
};

/** True quando o `addresstype` principal é um limite administrativo. */
export const isAdministrativeKind = (item: NominatimCityItem): boolean =>
  NON_CITY_KINDS.has((item.addresstype || "").toLowerCase().trim());

/**
 * True quando o resultado do geocoder representa uma cidade/localidade.
 *
 * Um resultado marcado como limite administrativo só é aceite quando,
 * cumulativamente: tem localidade válida no endereço, tem coordenadas finitas
 * e corresponde (após normalização) à cidade pedida. País/estado/região sem
 * localidade nunca passam — coordenadas por si só nunca chegam.
 */
export function isCityLevelResult(
  item: NominatimCityItem | null | undefined,
  requestedName?: string | null,
): boolean {
  if (!item) return false;
  if (!localityFromAddress(item.address)) return false;
  if (!hasFiniteCoords(item)) return false;
  if (!isAdministrativeKind(item)) return true;
  return Boolean(requestedName && matchesRequested(item, requestedName));
}

/** Identificador do fornecedor (OSM) quando existe; coordenadas são fallback. */
export function providerIdFromNominatim(item: NominatimCityItem): string | undefined {
  if (item.osm_type && item.osm_id != null) return `${item.osm_type}:${item.osm_id}`;
  if (item.place_id != null) return String(item.place_id);
  return undefined;
}


/**
 * Nome CANÓNICO (identidade/pesquisa): prefixos administrativos ("Greater
 * London", "Grande Londres", "City of London") e exónimos localizados
 * ("Moscou") voltam ao nome canónico da cidade pedida ("London", "Moscow").
 */
export function preferRequestedCityName(
  resolved: string | null | undefined,
  requested: string | null | undefined,
): string {
  const resolvedName = (resolved ?? "").trim();
  const requestedName = (requested ?? "").trim();
  if (!requestedName) return resolvedName;
  const canonical = canonicalCityName(requestedName);
  if (!resolvedName) return canonical;
  // Alias conhecido do mesmo sítio (Moscou/Moscovo/Moscow) → canónico.
  if (aliasKey(resolvedName) && aliasKey(resolvedName) === aliasKey(requestedName)) {
    return canonical;
  }
  const target = fold(resolvedName);
  const words = new Set(target.split(" ").filter(Boolean));
  for (const alias of [canonical, requestedName]) {
    const key = fold(alias);
    if (!key) continue;
    if (target === key) return canonical;
    // "grande londres" / "city of london" contêm a cidade pedida.
    if (words.has(key) || target.endsWith(` ${key}`) || target.startsWith(`${key} `)) {
      return canonical;
    }
  }
  return resolvedName;
}

/**
 * Nome de APRESENTAÇÃO: quando o utilizador escreveu um exónimo do mesmo
 * sítio ("Moscovo"), mostramos o que ele escreveu; caso contrário mostramos
 * o nome canónico. Nunca mostramos um exónimo de outra língua ("Moscou").
 */
export function displayCityName(
  canonicalName: string,
  requested: string | null | undefined,
): string {
  const requestedName = (requested ?? "").trim();
  if (!requestedName) return canonicalName;
  if (aliasKey(requestedName) === aliasKey(canonicalName)) return requestedName;
  return canonicalName;
}

export type PickedCity = {
  item: NominatimCityItem;
  lat: number;
  lng: number;
  /** Identidade/pesquisa (POIs, rotas, Maps). */
  city: string;
  /** Apresentação ao utilizador. */
  displayName: string;
  country: string;
  countryCode: string;
  provider: "osm";
  providerPlaceId?: string;
};

/**
 * Escolhe o melhor resultado ao nível cidade. Devolve `null` quando nenhum
 * resultado é uma localidade — nunca fazemos fallback para país/região.
 */
export function pickCityGeocodeResult(
  items: NominatimCityItem[] | null | undefined,
  requestedName: string,
  opts?: {
    /** Identidade já escolhida/pedida: nunca pode perder para um homónimo. */
    preferCountryCode?: string;
    preferPlaceId?: string;
  },
): PickedCity | null {
  const list = (items ?? []).filter((item) => isCityLevelResult(item, requestedName));
  if (!list.length) return null;
  const canonical = aliasKey(requestedName);
  const wantCountry = (opts?.preferCountryCode || "").toUpperCase();
  const wantPlaceId = (opts?.preferPlaceId || "").trim();
  const scored = list
    .map((item) => {
      const names = namesOf(item).map(aliasKey);
      const exact = names.some((name) => name === canonical);
      const partial = names.some(
        (name) => name.split(" ").includes(canonical) || name.includes(canonical),
      );
      // Resultado de cidade tem sempre prioridade sobre limite administrativo.
      const cityBonus = isAdministrativeKind(item) ? 0 : 4;
      // Identidade já fixada pelo utilizador (ou país escrito no pedido) manda
      // sempre: um homónimo noutro país nunca a pode ultrapassar.
      const identityBonus =
        (wantPlaceId && providerIdFromNominatim(item) === wantPlaceId ? 100 : 0) +
        (wantCountry && (item.address?.country_code || "").toUpperCase() === wantCountry ? 50 : 0);
      return { item, score: identityBonus + cityBonus + (exact ? 2 : partial ? 1 : 0) };
    })
    .sort((a, b) => b.score - a.score);
  const best = scored[0]?.item;
  if (!best) return null;
  const lat = Number(best.lat);
  const lng = Number(best.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const address = best.address ?? {};
  const raw =
    localityFromAddress(address) ||
    best.namedetails?.["name:en"] ||
    best.namedetails?.name ||
    requestedName;
  const city = preferRequestedCityName(raw, requestedName);
  return {
    item: best,
    lat,
    lng,
    city,
    displayName: displayCityName(city, requestedName),
    country: address.country || "",
    countryCode: (address.country_code || "").toUpperCase(),
    provider: "osm",
    providerPlaceId: providerIdFromNominatim(best),
  };
}


type GoogleComponent = { long_name?: string; short_name?: string; types?: string[] };

/** Só aceitamos `locality`/`postal_town` — nunca admin_area/país. */
export function googleCityFromComponents(
  components: GoogleComponent[] | null | undefined,
  requestedName: string,
): { city: string; displayName: string; country: string; countryCode: string } | null {
  const list = components ?? [];
  const locality =
    list.find((component) => component.types?.includes("locality"))?.long_name ||
    list.find((component) => component.types?.includes("postal_town"))?.long_name ||
    "";
  const countryComponent = list.find((component) => component.types?.includes("country"));
  const countryCode = (countryComponent?.short_name || "").toUpperCase();
  if (!locality || !countryCode) return null;
  const city = preferRequestedCityName(locality, requestedName);
  return {
    city,
    displayName: displayCityName(city, requestedName),
    country: countryComponent?.long_name || "",
    countryCode,
  };
}

